/* Knowledge Explorer sidebar panel — browse CPQ scripting API, events, patterns */

import { state, refs, esc, escAttr, setPanelLoading } from "./state.js";
import { api } from "./api.js";
import { getAuthMode } from "./auth.js";
import { insertAtCursor } from "./monaco-editor.js";

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let knowledgeTab = "classes";   // "classes" | "events" | "patterns"
let expandedClass = null;       // currently expanded class name
let expandedEvent = null;       // currently expanded event name
let editingPattern = null;      // {id, title, category, code} when editing, or "new"

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

export function initKnowledgePanel() {
  const el = document.getElementById("panel-knowledge");
  if (!el) return;

  // Tab buttons
  el.querySelectorAll(".kb-tab-btn").forEach(btn => {
    btn.addEventListener("click", () => switchKnowledgeTab(btn.dataset.tab));
  });

  // Search input
  const searchInput = el.querySelector("#kbSearchInput");
  if (searchInput) {
    let debounce;
    searchInput.addEventListener("input", () => {
      clearTimeout(debounce);
      debounce = setTimeout(() => loadCurrentTab(), 250);
    });
  }

  // Scope filter for events
  const scopeFilter = el.querySelector("#kbScopeFilter");
  if (scopeFilter) scopeFilter.addEventListener("change", () => loadCurrentTab());

  // Category filter for patterns
  const catFilter = el.querySelector("#kbCategoryFilter");
  if (catFilter) catFilter.addEventListener("change", () => loadCurrentTab());
}

// ---------------------------------------------------------------------------
// Tab switching
// ---------------------------------------------------------------------------

function switchKnowledgeTab(tab) {
  knowledgeTab = tab;
  const el = document.getElementById("panel-knowledge");
  if (!el) return;

  el.querySelectorAll(".kb-tab-btn").forEach(b =>
    b.classList.toggle("active", b.dataset.tab === tab)
  );
  el.querySelectorAll(".kb-tab-body").forEach(b =>
    b.hidden = b.dataset.tab !== tab
  );

  loadCurrentTab();
}

function loadCurrentTab() {
  if (knowledgeTab === "classes") loadClasses();
  else if (knowledgeTab === "events") loadEvents();
  else if (knowledgeTab === "patterns") loadPatterns();
}

// ---------------------------------------------------------------------------
// Load on first show
// ---------------------------------------------------------------------------

export function onKnowledgeActivated() {
  loadCurrentTab();
}

// ---------------------------------------------------------------------------
// Classes / Interfaces
// ---------------------------------------------------------------------------

async function loadClasses() {
  const el = document.getElementById("kbClassesList");
  if (!el) return;
  const search = document.getElementById("kbSearchInput")?.value || "";

  setPanelLoading('knowledge', true);
  try {
    const data = await api.knowledgeClasses(search);
    renderClassList(el, data.classes);
  } catch (err) {
    el.innerHTML = `<div class="kb-empty">Failed to load: ${esc(err.message)}</div>`;
  } finally {
    setPanelLoading('knowledge', false);
  }
}

function renderClassList(container, classes) {
  if (!classes.length) {
    container.innerHTML = '<div class="kb-empty">No types found</div>';
    return;
  }

  const html = classes.map(c => {
    const badge = c.kind === "enum" ? "enum"
      : c.kind === "interface" ? "iface" : "class";
    const counts = [];
    if (c.property_count) counts.push(`${c.property_count}p`);
    if (c.method_count) counts.push(`${c.method_count}m`);
    if (c.member_count) counts.push(`${c.member_count} vals`);
    const countStr = counts.length ? `<span class="kb-counts">${counts.join(" ")}</span>` : "";

    return `<div class="kb-item" data-name="${escAttr(c.name)}">
      <div class="kb-item-header">
        <span class="kb-badge kb-badge-${badge}">${badge}</span>
        <span class="kb-item-name">${esc(c.name)}</span>
        ${countStr}
        <button class="kb-insert-btn" data-insert="@${esc(c.name)}" title="Insert @ref into chat">@</button>
      </div>
      <div class="kb-item-detail" id="kbDetail-${esc(c.name)}" hidden></div>
    </div>`;
  }).join("");

  container.innerHTML = html;

  // Click to expand/collapse
  container.querySelectorAll(".kb-item-header").forEach(hdr => {
    hdr.addEventListener("click", (e) => {
      if (e.target.closest(".kb-insert-btn")) return;
      const name = hdr.parentElement.dataset.name;
      toggleClassDetail(name);
    });
  });

  // Insert button
  container.querySelectorAll(".kb-insert-btn").forEach(btn => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      insertIntoChat(btn.dataset.insert);
    });
  });
}

async function toggleClassDetail(name) {
  const detail = document.getElementById(`kbDetail-${name}`);
  if (!detail) return;

  if (!detail.hidden) {
    detail.hidden = true;
    expandedClass = null;
    return;
  }

  // Collapse previous
  if (expandedClass && expandedClass !== name) {
    const prev = document.getElementById(`kbDetail-${expandedClass}`);
    if (prev) prev.hidden = true;
  }
  expandedClass = name;

  detail.innerHTML = '<div class="kb-loading">Loading...</div>';
  detail.hidden = false;

  try {
    const data = await api.knowledgeClass(name);
    renderClassDetail(detail, data);
  } catch (err) {
    detail.innerHTML = `<div class="kb-empty">Error: ${esc(err.message)}</div>`;
  }
}

function renderClassDetail(container, data) {
  const parts = [];

  if (data.description) {
    parts.push(`<div class="kb-desc">${esc(data.description)}</div>`);
  }

  // Properties
  const props = data.properties || [];
  if (props.length) {
    parts.push(`<div class="kb-section-label">Properties (${props.length})</div>`);
    parts.push('<div class="kb-prop-list">');
    for (const p of props.slice(0, 50)) {
      const rw = p.is_settable ? '<span class="kb-rw">rw</span>' : '';
      parts.push(
        `<div class="kb-prop" data-insert="@${esc(data.name)}.${esc(p.name)}">` +
        `<span class="kb-prop-name">${esc(p.name)}</span> ${rw}` +
        `<span class="kb-prop-type">${esc(p.type)}</span>` +
        `</div>`
      );
    }
    if (props.length > 50) parts.push(`<div class="kb-more">...${props.length - 50} more</div>`);
    parts.push('</div>');
  }

  // Methods
  const methods = data.methods || [];
  if (methods.length) {
    parts.push(`<div class="kb-section-label">Methods (${methods.length})</div>`);
    parts.push('<div class="kb-method-list">');
    for (const m of methods.slice(0, 30)) {
      const params = (m.parameters || []).map(p => `${p.type} ${p.name}`).join(", ");
      const ret = m.return_type ? ` -> ${m.return_type}` : "";
      parts.push(
        `<div class="kb-method" data-insert="@${esc(data.name)}.${esc(m.name)}(${esc(params)})">` +
        `<span class="kb-method-name">${esc(m.name)}</span>` +
        `<span class="kb-method-sig">(${esc(params)})${esc(ret)}</span>` +
        `</div>`
      );
    }
    if (methods.length > 30) parts.push(`<div class="kb-more">...${methods.length - 30} more</div>`);
    parts.push('</div>');
  }

  // Enum members
  const members = data.enum_members || [];
  if (members.length) {
    parts.push(`<div class="kb-section-label">Values (${members.length})</div>`);
    for (const m of members) {
      parts.push(`<div class="kb-prop"><span class="kb-prop-name">${esc(m.name)}</span> = ${esc(m.value)}</div>`);
    }
  }

  container.innerHTML = parts.join("");

  // Click property/method to insert
  container.querySelectorAll("[data-insert]").forEach(el => {
    el.style.cursor = "pointer";
    el.addEventListener("click", () => insertIntoChat(el.dataset.insert));
  });
}

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

async function loadEvents() {
  const el = document.getElementById("kbEventsList");
  if (!el) return;
  const scope = document.getElementById("kbScopeFilter")?.value || "";

  setPanelLoading('knowledge', true);
  try {
    const data = await api.knowledgeEvents(scope);
    renderEventList(el, data.events);
  } catch (err) {
    el.innerHTML = `<div class="kb-empty">Failed to load: ${esc(err.message)}</div>`;
  } finally {
    setPanelLoading('knowledge', false);
  }
}

function renderEventList(container, events) {
  if (!events.length) {
    container.innerHTML = '<div class="kb-empty">No events found</div>';
    return;
  }

  // Group by scope
  const byScope = {};
  for (const ev of events) {
    const s = ev.scope || "other";
    (byScope[s] = byScope[s] || []).push(ev);
  }

  let html = "";
  for (const [scope, evs] of Object.entries(byScope)) {
    html += `<div class="kb-group-label">${esc(scope)} (${evs.length})</div>`;
    for (const ev of evs) {
      html += `<div class="kb-item kb-event-item" data-name="${escAttr(ev.name)}">
        <div class="kb-item-header">
          <span class="kb-badge kb-badge-event">${esc(scope.slice(0, 4))}</span>
          <span class="kb-item-name">${esc(ev.name)}</span>
          <button class="kb-insert-btn" data-insert="@${esc(ev.name)}" title="Insert @ref into chat">@</button>
        </div>
        <div class="kb-event-desc">${esc(ev.execution_point.slice(0, 100))}</div>
        <div class="kb-item-detail" id="kbEventDetail-${esc(ev.name)}" hidden></div>
      </div>`;
    }
  }

  container.innerHTML = html;

  container.querySelectorAll(".kb-insert-btn").forEach(btn => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      insertIntoChat(btn.dataset.insert);
    });
  });

  // Click to expand/collapse inline detail
  container.querySelectorAll(".kb-event-item .kb-item-header").forEach(hdr => {
    hdr.addEventListener("click", (e) => {
      if (e.target.closest(".kb-insert-btn")) return;
      const name = hdr.parentElement.dataset.name;
      toggleEventDetail(name);
    });
  });
}

async function toggleEventDetail(name) {
  const detail = document.getElementById(`kbEventDetail-${name}`);
  if (!detail) return;

  if (!detail.hidden) {
    detail.hidden = true;
    expandedEvent = null;
    return;
  }

  // Collapse previous
  if (expandedEvent && expandedEvent !== name) {
    const prev = document.getElementById(`kbEventDetail-${expandedEvent}`);
    if (prev) prev.hidden = true;
  }
  expandedEvent = name;

  detail.innerHTML = '<div class="kb-loading">Loading...</div>';
  detail.hidden = false;

  try {
    const data = await api.knowledgeEvent(name);
    renderEventDetail(detail, data);
  } catch (err) {
    detail.innerHTML = `<div class="kb-empty">Error: ${esc(err.message)}</div>`;
  }
}

function renderEventDetail(container, data) {
  const parts = [];

  if (data.execution_point) {
    parts.push(`<div class="kb-desc">${esc(data.execution_point)}</div>`);
  }

  // Arguments
  const args = data.arguments || [];
  if (args.length) {
    parts.push(`<div class="kb-section-label">Arguments (${args.length})</div>`);
    parts.push('<div class="kb-prop-list">');
    for (const a of args) {
      parts.push(
        `<div class="kb-prop" data-insert="@${esc(a.type)}" style="cursor:pointer">` +
        `<span class="kb-prop-name">${esc(a.name)}</span>` +
        `<span class="kb-prop-type">${esc(a.type)}</span>` +
        `</div>`
      );
    }
    parts.push('</div>');
  }

  // Notes
  const notes = data.notes || [];
  if (notes.length) {
    parts.push(`<div class="kb-section-label">Notes</div>`);
    for (const n of notes) {
      parts.push(`<div class="kb-event-note">${esc(n)}</div>`);
    }
  }

  // Related pipelines
  const pipelines = data.related_pipelines || [];
  if (pipelines.length) {
    parts.push(`<div class="kb-section-label">Pipelines (${pipelines.length})</div>`);
    parts.push('<div class="kb-prop-list">');
    for (const p of pipelines) {
      const ver = p.quote_version ? ` (v${p.quote_version})` : "";
      parts.push(`<div class="kb-prop"><span class="kb-prop-name">${esc(p.action)}${esc(ver)}</span></div>`);
    }
    parts.push('</div>');
  }

  // Related types
  const types = data.related_types || [];
  if (types.length) {
    parts.push(`<div class="kb-section-label">Related Types</div>`);
    parts.push('<div class="kb-prop-list">');
    for (const t of types) {
      parts.push(
        `<div class="kb-prop" data-insert="@${esc(t)}" style="cursor:pointer">` +
        `<span class="kb-prop-name">${esc(t)}</span>` +
        `</div>`
      );
    }
    parts.push('</div>');
  }

  container.innerHTML = parts.join("");

  // Click argument type or related type to insert
  container.querySelectorAll("[data-insert]").forEach(el => {
    el.addEventListener("click", () => insertIntoChat(el.dataset.insert));
  });
}

// ---------------------------------------------------------------------------
// Patterns
// ---------------------------------------------------------------------------

const PATTERN_CATEGORIES = [
  "sql", "rest_api", "quote", "product", "container", "integration", "utility"
];

async function loadPatterns() {
  const el = document.getElementById("kbPatternsList");
  if (!el) return;
  const category = document.getElementById("kbCategoryFilter")?.value || "";
  const search = document.getElementById("kbSearchInput")?.value || "";

  setPanelLoading('knowledge', true);
  try {
    const data = await api.knowledgePatterns(category, search);
    renderPatternList(el, data.patterns);
  } catch (err) {
    el.innerHTML = `<div class="kb-empty">Failed to load: ${esc(err.message)}</div>`;
  } finally {
    setPanelLoading('knowledge', false);
  }
}

function renderPatternList(container, patterns) {
  // Custom-pattern writes are local-only (the hosted server never registers the write
  // routes — the store is process-global), so hosted skips the add bar and the per-row
  // edit/delete actions entirely. Patterns still insert on click in both modes.
  const canWrite = getAuthMode() !== "hosted";
  // Add Pattern button + form slot at top
  let html = canWrite
    ? `<div class="kb-add-bar">
    <button class="kb-add-btn" id="kbAddPatternBtn">+ Add Pattern</button>
  </div>
  <div id="kbPatternFormSlot"></div>`
    : "";

  if (!patterns.length) {
    html += '<div class="kb-empty">No patterns found</div>';
  } else {
    html += patterns.map(p => {
      const isCustom = !!p.custom;
      const badge = isCustom ? "custom" : p.category;
      const badgeClass = isCustom ? "kb-badge-custom" : "kb-badge-pattern";
      const kws = (p.keywords || []).slice(0, 5).join(", ");
      const dataAttr = isCustom
        ? `data-custom-id="${escAttr(p.id)}"`
        : `data-builtin-index="${p.builtin_index}"`;
      const actions = (isCustom && canWrite)
        ? `<span class="kb-pattern-actions">
            <button class="kb-action-btn kb-edit-btn" title="Edit">&#9998;</button>
            <button class="kb-action-btn kb-delete-btn" title="Delete">&#128465;</button>
          </span>`
        : "";

      return `<div class="kb-item kb-pattern-item" ${dataAttr}>
        <div class="kb-item-header">
          <span class="kb-badge ${badgeClass}">${esc(badge)}</span>
          <span class="kb-item-name">${esc(p.title)}</span>
          ${actions}
        </div>
        ${p.description ? `<div class="kb-pattern-desc">${esc(p.description.slice(0, 120))}</div>` : ""}
        ${kws ? `<div class="kb-pattern-kws">${esc(kws)}</div>` : ""}
      </div>`;
    }).join("");
  }

  container.innerHTML = html;

  // Add Pattern button
  document.getElementById("kbAddPatternBtn")?.addEventListener("click", () => {
    showPatternForm(null);
  });

  // Click built-in pattern to insert code
  container.querySelectorAll(".kb-pattern-item[data-builtin-index]").forEach(el => {
    el.addEventListener("click", async (e) => {
      if (e.target.closest(".kb-action-btn")) return;
      const idx = parseInt(el.dataset.builtinIndex);
      try {
        const full = await api.knowledgePattern(idx);
        const blocks = full.code_blocks || [];
        const code = blocks.length ? blocks[0].code : "";
        if (!code) { insertIntoChat(full.title); return; }
        if (state.activeWorkspaceTab && insertAtCursor(code)) return;
        insertIntoChat(`\`\`\`python\n${code}\n\`\`\``);
      } catch { /* ignore */ }
    });
  });

  // Click custom pattern to insert code
  container.querySelectorAll(".kb-pattern-item[data-custom-id]").forEach(el => {
    el.addEventListener("click", (e) => {
      if (e.target.closest(".kb-action-btn")) return;
      const code = patterns.find(p => p.id === el.dataset.customId)?.code || "";
      if (!code) return;
      if (state.activeWorkspaceTab && insertAtCursor(code)) return;
      insertIntoChat(`\`\`\`python\n${code}\n\`\`\``);
    });
  });

  // Edit buttons
  container.querySelectorAll(".kb-edit-btn").forEach(btn => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const item = btn.closest(".kb-pattern-item");
      const id = item?.dataset?.customId;
      const p = patterns.find(pp => pp.id === id);
      if (p) showPatternForm(p);
    });
  });

  // Delete buttons
  container.querySelectorAll(".kb-delete-btn").forEach(btn => {
    btn.addEventListener("click", async (e) => {
      e.stopPropagation();
      const item = btn.closest(".kb-pattern-item");
      const id = item.dataset.customId;
      if (!confirm("Delete this custom pattern?")) return;
      try {
        await api.deletePattern(id);
        loadPatterns();
      } catch (err) {
        alert("Failed to delete: " + err.message);
      }
    });
  });
}

function showPatternForm(existing) {
  editingPattern = existing || "new";
  const slot = document.getElementById("kbPatternFormSlot");
  if (!slot) return;

  const title = existing?.title || "";
  const category = existing?.category || "utility";
  const code = existing?.code || "";

  const catOptions = PATTERN_CATEGORIES.map(c =>
    `<option value="${escAttr(c)}" ${c === category ? "selected" : ""}>${esc(c)}</option>`
  ).join("");

  slot.innerHTML = `<div class="kb-pattern-form">
    <input type="text" class="kb-form-input" id="kbPatFormTitle" placeholder="Pattern title" value="${escAttr(title)}" />
    <select class="kb-form-select" id="kbPatFormCategory">${catOptions}</select>
    <textarea class="kb-form-textarea" id="kbPatFormCode" rows="6" placeholder="Paste your code here...">${esc(code)}</textarea>
    <div class="kb-form-buttons">
      <button class="kb-form-btn kb-form-save" id="kbPatFormSave">${existing ? "Update" : "Save"}</button>
      <button class="kb-form-btn kb-form-cancel" id="kbPatFormCancel">Cancel</button>
    </div>
  </div>`;

  document.getElementById("kbPatFormSave")?.addEventListener("click", submitPatternForm);
  document.getElementById("kbPatFormCancel")?.addEventListener("click", () => {
    editingPattern = null;
    slot.innerHTML = "";
  });

  document.getElementById("kbPatFormTitle")?.focus();
}

async function submitPatternForm() {
  const title = document.getElementById("kbPatFormTitle")?.value.trim();
  const category = document.getElementById("kbPatFormCategory")?.value;
  const code = document.getElementById("kbPatFormCode")?.value.trim();

  if (!title) { alert("Title is required"); return; }
  if (!code) { alert("Code is required"); return; }

  try {
    if (editingPattern && editingPattern !== "new" && editingPattern.id) {
      await api.updatePattern(editingPattern.id, { title, category, code });
    } else {
      await api.createPattern({ title, category, code });
    }
    editingPattern = null;
    document.getElementById("kbPatternFormSlot").innerHTML = "";
    loadPatterns();
  } catch (err) {
    alert("Failed to save: " + err.message);
  }
}

// ---------------------------------------------------------------------------
// Insert into chat
// ---------------------------------------------------------------------------

// Exported: knowledge-view.js (the v2 workbench Knowledge view) reuses this
// exact implementation so both shells share one insert path (Monaco
// insert-at-cursor fast path + composer fallback).
export function insertIntoChat(text) {
  // If a file tab is open in Monaco, insert there first
  if (state.activeWorkspaceTab && insertAtCursor(text)) return;

  const input = document.getElementById("promptInput");
  if (!input) return;
  const before = input.value;
  const cursor = input.selectionStart || before.length;
  const prefix = before.slice(0, cursor);
  const suffix = before.slice(cursor);
  const spacer = prefix.length && !prefix.endsWith(" ") && !prefix.endsWith("\n") ? " " : "";
  input.value = prefix + spacer + text + suffix;
  input.focus();
  input.selectionStart = input.selectionEnd = cursor + spacer.length + text.length;
  // Trigger auto-resize if present
  input.dispatchEvent(new Event("input", { bubbles: true }));
}
